/* Insights charts: thin marks, hairline grid, 2 px gaps between fills, tokens only. */

.navy {
  --tone: var(--brand-navy);
}
.petrol {
  --tone: var(--brand-petrol);
}
.cyan {
  --tone: var(--brand-cyan);
}
.sky {
  --tone: var(--brand-sky);
}
.mist {
  --tone: var(--chart-year-1);
}
.gold {
  --tone: var(--brand-gold);
}
.trackTone {
  --tone: var(--chart-track);
}
.ghostTone {
  --tone: var(--chart-ghost);
}

.figure {
  margin: 0;
}

.summary {
  margin: 10px 0 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* --- Columns --- */

.columns {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
}

.yAxis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 180px;
  color: var(--text-muted);
  font-size: 11px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.yAxis span {
  line-height: 1;
  transform: translateY(-2px);
}

.plot {
  position: relative;
  min-width: 0;
}

.grid {
  position: absolute;
  inset: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 180px;
  pointer-events: none;
}

.grid span {
  display: block;
  border-top: 1px solid var(--chart-grid);
}

.columnRow {
  position: relative;
  display: flex;
  gap: 4px;
}

.slot {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: center;
  min-width: 0;
}

.columnTrack {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 180px;
}

.column {
  display: block;
  width: min(32px, 70%);
  min-height: 0;
  border-radius: 4px 4px 0 0;
  background: var(--tone);
  transition: height var(--dur-slow) var(--ease-out);
}

.columnLabel {
  margin-bottom: 4px;
  color: var(--brand-navy);
  font-size: 11px;
  font-weight: var(--fw-bold);
  white-space: nowrap;
}

.xLabel {
  margin-top: 6px;
  color: var(--text-muted);
  font-size: 11px;
  white-space: nowrap;
}

/* --- Bar rows --- */

.barRows {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.barRowsCompact {
  gap: 10px;
}

.barRow {
  display: grid;
  grid-template-columns: minmax(120px, 0.8fr) minmax(120px, 2fr) minmax(160px, 1.2fr);
  align-items: center;
  gap: 6px 16px;
}

@media (max-width: 720px) {
  .barRow {
    grid-template-columns: 1fr;
  }
}

.barLabel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.barName {
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  line-height: 1.35;
}

.barSub {
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.barStack {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.barLine {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.barArea {
  position: relative;
  flex: 1 1 auto;
  height: 14px;
  min-width: 0;
}

.track {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: 0 4px 4px 0;
  background: var(--chart-track);
}

.segments {
  position: absolute;
  inset: 0;
  display: flex;
  gap: 2px;
}

.segment {
  display: block;
  height: 100%;
  background: var(--tone);
  transition: width var(--dur-slow) var(--ease-out);
}

.segment:last-child {
  border-radius: 0 4px 4px 0;
}

.reference {
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 2px;
  margin-left: -1px;
  background: var(--brand-navy);
}

.barValueLabel {
  flex: 0 0 auto;
  color: var(--brand-navy);
  font-size: 12px;
  font-weight: var(--fw-bold);
  white-space: nowrap;
}

.barValue {
  color: var(--text-body);
  font-size: 13px;
  line-height: 1.45;
  text-align: right;
}

@media (max-width: 720px) {
  .barValue {
    text-align: left;
  }
}

.barAxis {
  display: grid;
  grid-template-columns: minmax(120px, 0.8fr) minmax(120px, 2fr) minmax(160px, 1.2fr);
  gap: 16px;
}

@media (max-width: 720px) {
  .barAxis {
    display: none;
  }
}

.barTicks {
  position: relative;
  height: 16px;
  border-top: 1px solid var(--chart-grid);
}

.barTicks span {
  position: absolute;
  top: 4px;
  color: var(--text-muted);
  font-size: 11px;
  transform: translateX(-50%);
  white-space: nowrap;
}

.barTicks span:first-child {
  transform: none;
}

.barTicks span:last-child {
  transform: translateX(-100%);
}

/* Stacked: name and value on one line, the bar under them (narrow side cards). */
.barRowsStacked .barRow {
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'label value'
    'bar bar';
}

.barRowsStacked .barLabel {
  grid-area: label;
}

.barRowsStacked .barStack {
  grid-area: bar;
}

.barRowsStacked .barValue {
  grid-area: value;
  text-align: right;
}

/* …and the axis runs the full width, under the bars. */
.barRowsStacked .barAxis {
  display: grid;
  grid-template-columns: 0 minmax(0, 1fr) 0;
  gap: 0;
}

/* On a phone the value goes under the name, so neither is squeezed. */
@media (max-width: 720px) {
  .barRowsStacked .barRow {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'label'
      'value'
      'bar';
  }

  .barRowsStacked .barValue {
    text-align: left;
  }
}

/* Tight: name, bar and value on one row in a narrow card, and on a phone too (short names). */
.barRowsTight .barRow,
.barRowsTight .barAxis,
.barRowsTight .barRefRow {
  grid-template-columns: minmax(0, 8.5rem) minmax(48px, 1fr) auto;
  gap: 4px 12px;
}

.barRowsTight .barAxis {
  display: grid;
}

.barRowsTight .barValue {
  min-width: 4.5rem;
  text-align: right;
}

/* The reference rule's name ("Company 7.4"), over the bars where the rule stands. */
.barRefRow {
  display: grid;
  grid-template-columns: minmax(120px, 0.8fr) minmax(120px, 2fr) minmax(160px, 1.2fr);
  gap: 16px;
  margin-bottom: -6px;
}

.barRefArea {
  position: relative;
  height: 16px;
}

.referenceLabel {
  position: absolute;
  bottom: 0;
  color: var(--brand-navy);
  font-size: 11px;
  font-weight: var(--fw-bold);
  white-space: nowrap;
  transform: translateX(-50%);
}

@media (max-width: 720px) {
  .barRefRow {
    grid-template-columns: 1fr;
  }

  .barRefRow > span {
    display: none;
  }

  .barRowsTight .barRefRow {
    grid-template-columns: minmax(0, 8.5rem) minmax(48px, 1fr) auto;
  }

  .barRowsTight .barRefRow > span {
    display: block;
  }
}

/* --- Lines --- */

/* The plot is measured and drawn in CSS pixels, so its 11 px words stay 11 px at any width. */
.linesBox {
  width: 100%;
  min-width: 0;
}

.lines {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.lineGrid {
  stroke: var(--chart-grid);
  stroke-width: 1;
}

.lineTick {
  fill: var(--text-muted);
  font-size: 11px;
}

.linePath {
  fill: none;
  stroke: var(--tone);
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.lineDot {
  fill: var(--tone);
  stroke: var(--white);
  stroke-width: 2;
}

.lineLabel {
  fill: var(--brand-navy);
  font-size: 12px;
  font-weight: var(--fw-bold);
}

.ghostLine {
  --tone: var(--chart-ghost);
}

/* --- Legend --- */

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0;
  padding: 0;
  color: var(--text-secondary);
  font-size: 12px;
  list-style: none;
}

.legend li {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 12px;
  height: 10px;
  border-radius: 2px;
  background: var(--tone);
}

.swatchLine {
  width: 16px;
  height: 2px;
  background: var(--tone);
}

/* --- Designed empty charts --- */

.ghost {
  position: relative;
}

/* The marks are grey; the words keep their full contrast. */
.ghostPlot {
  pointer-events: none;
}

.ghostNote {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(420px, 90%);
  padding: 14px 18px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-sm);
  text-align: center;
  transform: translate(-50%, -50%);
}

.ghostHeadline {
  margin: 0 0 4px;
  color: var(--brand-navy);
  font-size: 15px;
  font-weight: var(--fw-bold);
}

.ghostBody {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* Bar rows keep their names and values readable: the note sits above them instead. */
.ghostAbove {
  display: flex;
  flex-direction: column-reverse;
  gap: 12px;
}

.ghostAbove .ghostNote {
  position: static;
  width: auto;
  transform: none;
  box-shadow: none;
}
